import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  BodyText,
  Button,
  DataGrid,
  EmptyState,
  Tabs,
  type ColumnDef,
  type TabItem,
} from '@aaimplatform/ui';
import type { components as SyndicationComponents } from '@aaimplatform/institutional-types/generated/loan-syndication-protocol';
import { useParticipationOfferings } from '../../hooks/useLoanSyndication';
import { ABSENT, formatCount, formatMoney, formatRatioAsPercent } from '../../utils/display-format';
import {
  OFFERING_STATUS_LABEL,
  OFFERINGS_SCOPE_NOTE,
  collateralAssetClassLabel,
  collateralLtvLabel,
} from './syndication-display';

type ParticipationOffering = SyndicationComponents['schemas']['ParticipationOffering'];

/**
 * The unfiltered tab. With no status the service answers its default set for
 * a non-lead caller (OPEN, ACTIVE, FULLY_COMMITTED); the tab was called "All"
 * while it omitted every matured, defaulted and canceled offering.
 */
const CURRENT_TAB = 'current';

/**
 * Status tabs. Each status tab is sent to the server as the status query, so
 * a tab reads that status from the service rather than filtering the default
 * set in the browser.
 */
const STATUS_TABS: readonly ParticipationOffering['status'][] = [
  'OPEN',
  'FULLY_COMMITTED',
  'ACTIVE',
  'MATURED',
];

const STATUS_FILTER_TABS: TabItem[] = [
  { id: CURRENT_TAB, label: 'Current' },
  ...STATUS_TABS.map((status) => ({ id: status, label: OFFERING_STATUS_LABEL[status] })),
];

function tabStatus(tab: string): ParticipationOffering['status'] | undefined {
  return STATUS_TABS.find((status) => status === tab);
}

/** Below this width the grid scrolls sideways instead of overprinting headers. */
const GRID_MIN_WIDTH = 960;

function useColumns(): ColumnDef<ParticipationOffering, unknown>[] {
  return useMemo<ColumnDef<ParticipationOffering, unknown>[]>(
    () => [
      {
        id: 'offeringId',
        header: 'Offering',
        accessorFn: (row) => row.offeringId,
        cell: ({ row }) => (
          <span className="block truncate font-mono text-xs" title={row.original.offeringId}>
            {row.original.offeringId}
          </span>
        ),
        size: 130,
      },
      {
        id: 'leadInstitution',
        header: 'Lead FI',
        accessorFn: (row) => row.leadInstitutionName ?? ABSENT,
        size: 170,
      },
      {
        id: 'syndicationAmount',
        header: 'Syndication Amount',
        // Spec-required (ParticipationStructure.totalSyndicationAmount). A
        // table cell prints the full amount; compact figures are for tiles.
        accessorFn: (row) => row.participationStructure.totalSyndicationAmount,
        cell: ({ row }) =>
          formatMoney(row.original.participationStructure.totalSyndicationAmount),
        size: 150,
      },
      {
        id: 'interestRate',
        header: 'Rate',
        // Spec-required (LoanDetails.interestRate), a ratio: 0.065 is 6.5%.
        accessorFn: (row) => row.loanDetails.interestRate,
        cell: ({ row }) => formatRatioAsPercent(row.original.loanDetails.interestRate),
        size: 80,
      },
      {
        id: 'collateralType',
        header: 'Collateral',
        accessorFn: (row) => collateralAssetClassLabel(row.collateralSummary),
        size: 150,
      },
      {
        id: 'ltvRatio',
        header: 'LTV',
        accessorFn: (row) => collateralLtvLabel(row.collateralSummary),
        size: 100,
      },
      {
        id: 'remainingCapacity',
        header: 'Remaining',
        // Spec-required; a fully committed offering has a real $0.00.
        accessorFn: (row) => row.remainingCapacity,
        cell: ({ row }) => formatMoney(row.original.remainingCapacity),
        size: 140,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <Badge variant="neutral">{OFFERING_STATUS_LABEL[row.original.status]}</Badge>
        ),
        size: 140,
      },
    ],
    [],
  );
}

function emptyCopy(status: ParticipationOffering['status'] | undefined): {
  title: string;
  description: string;
} {
  if (!status) {
    return {
      title: 'No current offerings',
      description:
        'Current covers Open, Fully Committed and Active offerings. An offering appears here when the Aaim platform tenant lists a loan participation on the Liquidity Network.',
    };
  }
  return {
    title: `No offerings with status ${OFFERING_STATUS_LABEL[status]}`,
    description: 'No offering originated in the Aaim platform tenant is in this status.',
  };
}

export function SyndicationOfferings() {
  const [statusFilter, setStatusFilter] = useState<string>(CURRENT_TAB);
  const status = tabStatus(statusFilter);
  const { data, isLoading, error, refetch, isFetching } = useParticipationOfferings(status);
  const columns = useColumns();

  const offerings = data?.offerings ?? [];
  const totalItems = data?.pagination.totalItems;
  const partial = typeof totalItems === 'number' && totalItems > offerings.length;

  let body;
  if (error) {
    body = (
      <Alert type="error" title="Participation offerings could not be loaded">
        <p className="mb-3">{error.message}</p>
        <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
          Retry
        </Button>
      </Alert>
    );
  } else if (isLoading) {
    body = (
      <DataGrid<ParticipationOffering>
        data={[]}
        columns={columns}
        isLoading
        skeletonRowCount={5}
        fixedLayout
        minTableWidth={GRID_MIN_WIDTH}
        className="border border-recessed"
      />
    );
  } else if (offerings.length === 0) {
    const copy = emptyCopy(status);
    body = <EmptyState title={copy.title} description={copy.description} />;
  } else {
    body = (
      <DataGrid<ParticipationOffering>
        data={offerings}
        columns={columns}
        getRowId={(row) => row.offeringId}
        fixedLayout
        minTableWidth={GRID_MIN_WIDTH}
        className="border border-recessed"
      />
    );
  }

  return (
    <div>
      {/*
        "Create Offering" Save-button affordance removed (Sprint C11 Lane 5):
        there was no onClick, no mutation, no backend route. Per Save Button
        Discipline rule (codebot/.claude/rules/ui-package.md), an unwired
        mutation button is forbidden. Restore once the loan-syndication
        backend ships POST /api/v1/syndication/offerings.
      */}
      {/* The read is the caller's own tenant (GET /offerings); the page says
          so instead of presenting that book as the network. */}
      <BodyText variant="ui" className="text-sm text-tertiary mb-4">
        {OFFERINGS_SCOPE_NOTE}
      </BodyText>
      <div className="mb-4">
        <Tabs
          tabs={STATUS_FILTER_TABS}
          activeTab={statusFilter}
          onTabChange={setStatusFilter}
        />
      </div>

      {body}

      {partial && !error && (
        <BodyText variant="ui" className="text-sm text-tertiary mt-3">
          Showing the first {formatCount(offerings.length)} of {formatCount(totalItems)} offerings.
        </BodyText>
      )}
    </div>
  );
}
